<script setup lang="ts">
import DatePickerWrapper from '~/components/DatePickerWrapper.vue'
import ChartDownloadButton from '~/components/price-assessments/ChartDownloadButton.vue'
import { useModalStore } from '~/stores/modal'
import { isLithiumIonBatteries } from '~/src/utils/download-xlsx-config'
import {
  downloadBlob,
  objectToXlsxInGroupsServicePro,
  objectToXlsxOneSheet,
} from '~/src/utils/download-xlsx'
import { logError } from '~/utils/log-error'
import { format, parseISO } from 'date-fns'
import { useDownloadChart } from '~/composables/useDownloadChart'

const props = defineProps<{
  title: string
  minDate: Date
  startDate: string
  endDate: string
  category: string
  dataForDownload: any
  rawData: any
  authorized: boolean
  showOverlay: boolean
  disableDownloadButton: boolean
}>()

const emit = defineEmits(['updateSelectedDates'])

const { $rudderstack } = useNuxtApp()
const { open: openModal } = useModalStore()
const { downloadChart } = useDownloadChart()

const getXlsxBlob = () => {
  if (isLithiumIonBatteries(props.category)) {
    return objectToXlsxOneSheet(
      props.category,
      props.dataForDownload,
      props.title,
      props.startDate,
      props.endDate,
    )
  }

  return objectToXlsxInGroupsServicePro(props.category, props.dataForDownload)
}

const downloadAsXlsx = () => {
  try {
    const blob = getXlsxBlob()
    downloadBlob(blob, props.startDate, props.endDate, props.title)

    $rudderstack.trackChartDownload({
      chart_type: 'lines',
      chart_title: props.title,
      download_format: 'xlsx',
    })
  } catch (e) {
    logError(e)
  }
}

const downloadAsPNGImage = () => {
  const endDateFormatted = format(parseISO(props.endDate), 'yyyy-MM-dd')
  const startDateFormatted = format(parseISO(props.startDate), 'yyyy-MM-dd')
  const pngName = `benchmark-${props.title
    .toLowerCase()
    .replaceAll(
      ' ',
      '-',
    )}-chart-${startDateFormatted}-${endDateFormatted}-${new Date().getTime()}`
  downloadChart({
    name: pngName,
    watermarkLocations: ['center'],
    data: props.rawData,
  })
}

const downloadChartByType = (type: string) => {
  const types = {
    xlsx: downloadAsXlsx,
    png: downloadAsPNGImage,
  }

  const downloadType = types[type]

  if (!downloadType) return

  downloadType()
}

const openFiltersModal = () => {
  openModal('priceAssessmentFilters')
}
</script>

<template>
  <div>
    <div
      class="mb-4 flex flex-col lg:mb-0 lg:flex-row lg:items-center lg:justify-between"
    >
      <!-- Chart Title -->
      <div
        class="z-30 flex flex-grow flex-col gap-x-3 pb-3 lg:absolute lg:bottom-full lg:right-0 lg:flex-row lg:pb-0"
      >
        <button
          class="remove-me mb-3 rounded border border-grey-600 bg-white px-4 py-2 font-semibold text-s-900 hover:border-p-600 active:border-p-600 disabled:pointer-events-none disabled:opacity-25 lg:hidden"
          :disabled="!authorized"
          @click="openFiltersModal"
        >
          <span
            class="mr-3 w-full text-left font-montserrat text-sm font-semibold lg:w-auto lg:text-xs"
          >
            Edit Chart
          </span>
        </button>
        <ChartDownloadButton
          v-if="authorized"
          class="remove-me"
          :disabled="disableDownloadButton"
          @download="downloadChartByType"
        />
      </div>
    </div>

    <div class="hidden flex-row lg:mb-4 lg:flex justify-between items-center">
      <slot name="extra-actions" />

      <ClientOnly>
        <DatePickerWrapper
          data-testid="start-date"
          :min-date="minDate"
          :start-date="startDate"
          :end-date="endDate"
          :disabled="showOverlay"
          @date-range-updated="emit('updateSelectedDates', $event)"
        />
      </ClientOnly>
    </div>
  </div>
</template>
